import { Remarkable } from 'remarkable';
import { linkify } from 'remarkable/linkify';
import { TranslateService } from '@ngx-translate/core';
import {
  WH_AI_THUMBS_DOWN_SVG,
  WH_AI_THUMBS_UP_SVG,
} from 'src/app/app-modules-pages/workheld-ai/config/wh-ai-icons';

type AiUrlCitation = { url?: string; title?: string };

type AiAnnotation = {
  type?: string;
  text?: string;
  urlCitation?: AiUrlCitation;
};

export type AiHtmlResponseOptions = {
  runId?: string;
  questionText?: string;
  enableFeedback?: boolean;
  createdAt?: string;
  locale?: string;
  sourcesLabel?: string;
};

const markdownRenderer = new Remarkable({
  html: false,
  breaks: true,
}).use(linkify);

const ICON_THUMBS_UP = WH_AI_THUMBS_UP_SVG;

const ICON_THUMBS_DOWN = WH_AI_THUMBS_DOWN_SVG;

const CITATION_RE = /\u3010[^\u3011]*\u3011/g;
const CITATION_FILE_RE_G = /\u3010[^†]*†([^\u3011]+)\u3011/g;
const SOURCES_SECTION_RE =
  /(?:^|\n)\s*(?:[#>*-]+\s*)?(?:\*\*)?[^\n]*?(?:Quellen|Sources|Quellenangaben|References)[^\n]*?(?:\*\*)?\s*:?\s*\n\s*[-–•*][\s\S]*$/i;
const SOURCE_MARKDOWN_LINK_RE = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
const SOURCE_URL_RE = /https?:\/\/[^\s,)]+/g;

export function extractTextAndAnnotations(payload: any): {
  text: string;
  annotations: AiAnnotation[];
} {
  const visited = new Set<any>();

  const read = (node: any): { text?: string; annotations?: AiAnnotation[] } => {
    if (!node || typeof node !== 'object' || visited.has(node)) return {};
    visited.add(node);

    if (node.annotatedResponse) return read(node.annotatedResponse);
    if (typeof node.value === 'string')
      return { text: node.value, annotations: node.annotations ?? [] };
    if (typeof node.text?.value === 'string')
      return {
        text: node.text.value,
        annotations: node.text.annotations ?? [],
      };
    if (Array.isArray(node.content) && node.content.length)
      return read(node.content[0]);
    if (Array.isArray(node)) {
      for (const item of node) {
        const r = read(item);
        if (r.text) return r;
      }
    }
    if (node.message) return read(node.message);
    if (node.data) return read(node.data);
    return {};
  };

  const result = read(payload);
  return {
    text: typeof result.text === 'string' ? result.text : '',
    annotations: Array.isArray(result.annotations) ? result.annotations : [],
  };
}

export function buildAiHtmlResponse(
  payload: any,
  options?: AiHtmlResponseOptions,
): string {
  const { text, annotations } = extractTextAndAnnotations(payload);

  const citationSources = extractCitationFiles(text);

  const withoutCitations = (text.replace(CITATION_RE, '') ?? '').trim();

  const extractedSources = extractSourcesFromText(withoutCitations);
  const responseText = extractedSources.text;
  const runId = options?.runId?.trim() ?? '';
  const questionText = options?.questionText?.trim() ?? '';
  const createdAt = options?.createdAt?.trim() ?? '';
  const locale = options?.locale?.trim() ?? '';

  const responseBody = `<div class="wh-ai-response-text">${renderMarkdown(responseText)}</div>`;

  const sources = mergeSources(
    normalizeSources(annotations),
    extractedSources.urls,
    citationSources,
  );

  const sourcesHtml =
    sources.length > 0
      ? buildSourcesSummarySection(sources, options?.sourcesLabel ?? '')
      : '';

  const feedbackHtml =
    options?.enableFeedback && runId && questionText
      ? buildFeedbackSection(runId, questionText, responseText)
      : '';

  const footer = buildFooter(formatDate(createdAt, locale), feedbackHtml);

  return `<div class="wh-ai-response">${responseBody}${sourcesHtml}${footer}</div>`;
}

export function renderAiMarkdown(
  text: string,
  extractingLabel?: string,
): string {
  void extractingLabel;
  const cleaned = text.replace(CITATION_RE, '');
  return renderMarkdown(cleaned);
}

export function getSourcesLabel(translate: TranslateService): string {
  const key = 'app.ai.sources.title.label';
  const translated = translate.instant(key)?.trim();
  if (translated && translated !== key) {
    return translated;
  }
  const lang = translate.currentLang || translate.defaultLang || '';
  return lang.startsWith('de') ? 'Quellen' : 'Sources';
}

function renderMarkdown(text: string): string {
  if (!text) {
    return '';
  }

  const rendered = markdownRenderer.render(text);
  return decorateSourceLinks(rendered);
}

function normalizeSources(annotations: AiAnnotation[]): AiUrlCitation[] {
  const normalized = new Map<string, AiUrlCitation>();

  annotations.forEach((a) => {
    const url = a?.type === 'url_citation' ? a.urlCitation?.url?.trim() : '';
    if (!url) {
      return;
    }

    const title = normalizeSourceTitle(a.urlCitation?.title?.trim() || '', url);
    upsertSource(normalized, { url, title });
  });

  return Array.from(normalized.values());
}

function extractCitationFiles(text: string): AiUrlCitation[] {
  const seen = new Set<string>();
  const files: AiUrlCitation[] = [];
  for (const match of text.matchAll(CITATION_FILE_RE_G)) {
    const filename = match[1].trim();
    if (filename && !seen.has(filename) && !isCitationPlaceholder(filename)) {
      seen.add(filename);
      files.push({
        title: normalizeSourceTitle(filename, filename),
        url: filename,
      });
    }
  }
  return files;
}

function extractSourcesFromText(text: string): {
  text: string;
  urls: AiUrlCitation[];
} {
  const match = text.match(SOURCES_SECTION_RE);
  if (!match) {
    return { text: text.trim(), urls: [] };
  }

  const sourcesBlock = match[0];
  const seen = new Set<string>();
  const urls: AiUrlCitation[] = [];

  for (const markdownMatch of sourcesBlock.matchAll(SOURCE_MARKDOWN_LINK_RE)) {
    const title = markdownMatch[1].trim();
    const url = markdownMatch[2].replace(/[.)]+$/, '').trim();
    if (!url || seen.has(url)) {
      continue;
    }

    seen.add(url);
    urls.push({ url, title: normalizeSourceTitle(title, url) });
  }

  for (const urlMatch of sourcesBlock.matchAll(SOURCE_URL_RE)) {
    const url = urlMatch[0].replace(/[.)]+$/, '');
    if (!seen.has(url)) {
      seen.add(url);
      urls.push({ url, title: normalizeSourceTitle('', url) });
    }
  }

  // Strip the sources section from the body to avoid duplication with source chips
  const responseText = text.replace(SOURCES_SECTION_RE, '').trim();

  return { text: responseText, urls };
}

function buildSourcesSummarySection(
  sources: AiUrlCitation[],
  label: string,
): string {
  const safeLabel = label.trim() || 'Sources';

  const items = sources
    .map(
      (s, i) => `
        <li class="wh-ai-source-item" style="animation-delay: ${i * 70}ms">
          <button type="button" class="wh-ai-source-card" data-doc-url="${esc(s.url ?? '')}">
            <div class="wh-ai-source-card-header">
              <span class="wh-ai-source-index">${i + 1}</span>
              <span class="wh-ai-source-title">${escHtml(s.title ?? '')}</span>
            </div>
          </button>
        </li>`,
    )
    .join('');

  return `
    <div class="wh-ai-sources wh-ai-sources--fade-in">
      <div class="wh-ai-sources-title">${escHtml(safeLabel)}</div>
      <ul class="wh-ai-sources-list">${items}</ul>
    </div>`;
}

function mergeSources(...groups: AiUrlCitation[][]): AiUrlCitation[] {
  const merged = new Map<string, AiUrlCitation>();

  groups.flat().forEach((source) => upsertSource(merged, source));

  return Array.from(merged.values()).filter((s) => isValidSourceUrl(s.url));
}

const CITATION_PLACEHOLDERS = new Set([
  'source',
  'titel',
  'title',
  'quelle',
  'document',
  'file',
]);

function isCitationPlaceholder(filename: string): boolean {
  const lower = filename.toLowerCase().trim();
  return CITATION_PLACEHOLDERS.has(lower);
}

function isValidSourceUrl(url: string | undefined): boolean {
  if (!url) return false;
  const trimmed = url.trim();
  if (!trimmed) return false;
  if (isCitationPlaceholder(trimmed)) return false;
  if (trimmed.startsWith('mcp://')) return false;
  // Accept HTTP(S) URLs and file-like references (e.g. "ALL_101deE_a.pdf")
  if (/^https?:\/\//i.test(trimmed)) return true;
  // Accept filename-like values (contain a dot suggesting an extension)
  if (/\.\w{2,5}$/.test(trimmed)) return true;
  return false;
}

function upsertSource(
  map: Map<string, AiUrlCitation>,
  source: AiUrlCitation,
): void {
  const url = source.url?.trim() ?? '';
  if (!url) {
    return;
  }

  const normalizedTitle = normalizeSourceTitle(source.title?.trim() ?? '', url);
  const existing = map.get(url);

  if (!existing) {
    map.set(url, { url, title: normalizedTitle });
    return;
  }

  const existingTitle = existing.title?.trim() ?? '';
  if (sourceTitleScore(normalizedTitle) > sourceTitleScore(existingTitle)) {
    map.set(url, { ...existing, title: normalizedTitle });
  }
}

function sourceTitleScore(title: string): number {
  const normalized = title.trim();
  if (!normalized) {
    return 0;
  }
  return 1;
}

function normalizeSourceTitle(title: string, url: string): string {
  const normalizedTitle = title.trim();
  if (normalizedTitle) {
    return normalizedTitle;
  }

  const rawFilename =
    url
      .split('/')
      .pop()
      ?.replace(/\.[^.]+$/, '') ?? url;

  try {
    return decodeURIComponent(rawFilename);
  } catch {
    return rawFilename;
  }
}

function decorateSourceLinks(html: string): string {
  return html.replace(
    /<a\s+([^>]*?)>/gi,
    (fullMatch, rawAttributes: string) => {
      const hrefMatch = rawAttributes.match(/\bhref="([^"]+)"/i);
      if (!hrefMatch) {
        return fullMatch;
      }

      const escapedHref = hrefMatch[1];
      const classMatch = rawAttributes.match(/\bclass="([^"]*)"/i);
      const existingClass = classMatch?.[1]?.trim() ?? '';
      const mergedClass = [
        existingClass,
        'wh-ai-source-link',
        'wh-ai-source-link--inline',
      ]
        .filter(Boolean)
        .join(' ');

      let attributes = rawAttributes.replace(/\bclass="[^"]*"\s*/i, '').trim();

      if (
        /^https?:\/\//i.test(escapedHref) &&
        /core\.windows\.net/i.test(escapedHref) &&
        !/\bdata-doc-url="/i.test(attributes)
      ) {
        // Decode HTML entities from the href and re-escape for attribute context
        const decodedHref = decodeHtmlEntities(escapedHref);
        attributes += ` data-doc-url="${esc(decodedHref)}"`;
      }

      return `<a class="${escHtml(mergedClass)}"${attributes ? ` ${attributes}` : ''}>`;
    },
  );
}

function buildFeedbackSection(
  runId: string,
  question: string,
  answer: string,
): string {
  return `
    <div class="wh-ai-feedback"
      data-run-id="${esc(runId)}"
      data-question="${esc(question)}"
      data-answer="${esc(answer)}">
      <button type="button" class="wh-ai-feedback-button"
        data-feedback-value="positive" data-cy="wh-ai-feedback-like" aria-label="Thumbs up">
        ${ICON_THUMBS_UP}
      </button>
      <button type="button" class="wh-ai-feedback-button"
        data-feedback-value="negative" data-cy="wh-ai-feedback-dislike" aria-label="Thumbs down">
        ${ICON_THUMBS_DOWN}
      </button>
    </div>`;
}

function buildFooter(dateLabel: string, feedback: string): string {
  if (!dateLabel && !feedback) return '';
  const date = dateLabel
    ? `<span class="wh-ai-response-date">${escHtml(dateLabel)}</span>`
    : '<span></span>';
  return `<div class="wh-ai-response-footer">${date}${feedback}</div>`;
}

function formatDate(value: string, locale: string): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(locale || undefined, {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(d);
}

function escHtml(v: string): string {
  return v
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function esc(v: string): string {
  return escHtml(v).replace(/`/g, '&#96;').replace(/\n/g, '&#10;');
}

function decodeHtmlEntities(text: string): string {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
}
